<template>
  <section class="i-faq">
    <div class="container">
      <div class="label mb-3">Questions</div>
      <h2
        class="headline"
        style="margin-bottom: 32px"
      >
        The things people ask<br />before they book.
      </h2>

      <div
        v-for="item in faqs"
        :key="item.q"
        class="faq__item"
      >
        <div class="faq__q">{{ item.q }}</div>
        <div class="faq__a">{{ item.a }}</div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue"

// PROPS
const props = defineProps({
  pricing: {
    type: String,
    default: "49"
  },
  timing: {
    type: String,
    default: "30"
  }
})

// COMPUTED
const faqs = computed(() => {
  return [
    {
      q: `Is ${props.timing} minutes really enough time to be useful?`,
      a: `Yes — because the session is engineered, not improvised. Your coach reviews your report before the call. They arrive with observations. You arrive with context. The ${props.timing} minutes are the most efficient ${props.timing} minutes of advice you'll receive on this topic because they're not discovery time — they're delivery time. That said: if your situation is unusually complex and ${props.timing} minutes isn't sufficient, your coach will tell you that on the call, and you'll receive a refund.`
    },
    {
      q: "What if I'm not ready to make a move yet?",
      a: 'This session is ideal for that situation. "Not ready yet" usually means "I don\'t know the exact path." The session gives you that path — so you know what you\'re building toward, what to work on in the next 6 months, and when the timing is right to move. Many clients book 6–12 months before their intended transition specifically for this reason.'
    },
    {
      q: "How is this different from just reading career advice online?",
      a: 'Generic advice — "update your LinkedIn," "network more," "tailor your resume" — is universally available and uniformly useless because it doesn\'t apply to your specific situation, your specific title target, or what\'s actually in your profile. The session is specific. The output is specific. Generic inputs produce generic outputs. Specific inputs — your report, your situation — produce specific outputs. That\'s the difference.'
    },
    {
      q: "What if I don't like the session?",
      a: `Full refund. If you finish the session without at least 3 specific, actionable steps you can act on this week, email us and we'll refund the full ${props.pricing} immediately. No form, no process, no minimum wait. We're confident you won't need it — but the risk is ours, not yours.`
    },
    {
      q: "Will I be sold anything during or after the session?",
      a: "No. This is not a lead-generation mechanism for more services. Our coaches have no commission, no quota, and no package to pitch. The session ends. You leave with your plan. You will never receive a follow-up sales call, a promotional email based on your session, or an offer for a retainer. We don't offer retainers. This is the only product in this category that we sell."
    }
  ]
})
</script>

<style lang="scss" scoped>
.i-faq {
  .faq {
    &__item {
      border-bottom: 1px solid var(--border);
      padding: 24px 0;

      &:first-child {
        border-top: 1px solid var(--border);
      }
    }

    &__q {
      font-weight: 700;
      font-size: 0.95rem;
      color: var(--text);
      margin-bottom: 10px;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 16px;

      &::after {
        content: "+";
        font-size: 1.2rem;
        font-weight: 300;
        color: var(--muted);
        flex-shrink: 0;
        margin-top: -2px;
      }
    }

    &__a {
      font-size: 0.875rem;
      color: var(--text-body);
      line-height: 1.7;
    }
  }
}
</style>
